// src/components/MenuViewer/MenuViewer.tsx
import React, { useState, useCallback,useEffect, useRef} from 'react';
import { Modal, Button,Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { RemoveLocalMenuPdfByLocalId,
    getMenuPdfByLocalId,
    SetLocalMenuPdfByLocalId } from '../../../../../../../../crud/menu_api/menus';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './MenuViewer.scss';
import { FaTrash, FaPaperPlane } from 'react-icons/fa';

interface MenuViewerProps {
    config: any
}

const MenuViewer: React.FC<MenuViewerProps> = ({config}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [opening, setOpening] = useState(false);
  const [menuUrl, setMenuUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)
  const [nameUrl, setNameUrl] = useState<string | null>(null)

  // load both site‐config and menu‐pdf on mount
    useEffect(() => {
      setLoading(true)
      const u = JSON.parse(localStorage.getItem('user') || '{}')
      const localId = u.local_id || 0
      getMenuPdfByLocalId(localId).then(r => {
        const pdfUrl = r.data.menu_url
        setMenuUrl(pdfUrl)
      }
      ).catch(() => {
        setMenuUrl(null)
      }).finally(() => {
        setLoading(false)
      }
      )

    }, [])

  const handleRemoveClick = () => setShowConfirm(true);

  const handleConfirm = async () => {
    setRemoving(true);
    try {
      const u = localStorage.getItem('user');
      const localId = u ? JSON.parse(u).local_id : 0;
      await RemoveLocalMenuPdfByLocalId(localId);
      toast.success('Menú eliminado correctamente');
      setMenuUrl(null);
    } catch (err) {
      console.error(err);
      toast.error('Error al eliminar el menú');
    } finally {
      setRemoving(false);
      setShowConfirm(false);
    }
  };

  const openMenuInBrowser = useCallback(async () => {
    if (!menuUrl) {
      toast.error('No hay menú disponible para abrir');
      return;
    }
    setOpening(true);
    try {
      const res = await fetch(menuUrl, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // pull down as ArrayBuffer so we can re-type it
      const buffer = await res.arrayBuffer();
      const pdfBlob = new Blob([buffer], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
      // clean up after 1 minute
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('Failed to open PDF:', err);
      toast.error('No se pudo abrir el menú en el navegador');
    } finally {
      setOpening(false);
    }
  }, [menuUrl]);

  // file → base64 → upload
    const onFileChange = (e:React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !config) return
      const reader = new FileReader()
      reader.onload = async () => {
        const b64 = (reader.result as string).split(',')[1]
        setUploading(true)
        try {
          const u = localStorage.getItem('user');
          const localId = u ? JSON.parse(u).local_id : 0;
          await SetLocalMenuPdfByLocalId(localId, b64)
          toast.success('Menú PDF subido correctamente')
          setMenuUrl(URL.createObjectURL(file))
          setNameUrl("Menú PDF")
        } catch {
          toast.error('Error al subir el menú PDF')
        } finally {
          setUploading(false)
        }
      }
      reader.readAsDataURL(file)
    }

    if (loading) {
      return (
        <div className="menu-viewer">
          <MiniLoadingSpinner/>
        </div>
      )
    }

  return (
    <div className="menu-viewer">
        {menuUrl ? (
      <div className="menu-container">
        <span>
            {/*extract the /<>.pdf name of the url */}
            {nameUrl ? nameUrl : menuUrl.split('/').pop()?.split('?')[0]}
            {/**  */}
        </span>
        <Button
          variant="none"
          onClick={openMenuInBrowser}
          disabled={opening}
          className="action-btn open-menu-btn"
        >
          {opening
            ? <MiniLoadingSpinner/>
            : <>Abrir Menú en nueva pestaña <FaPaperPlane/></>
          }
        </Button>

        <Button
          variant="none"
          onClick={handleRemoveClick}
          disabled={removing}
          className="action-btn"
        >
          {removing
            ? <MiniLoadingSpinner/>
            : <>Eliminar <FaTrash/></>
          }
        </Button>
      </div>) : (
        <Form>
        <Form.Group>
          <Form.Label>Subir Menú (PDF)</Form.Label>
          <div className="upload-wrapper">
            <Form.Control
              type="file"
              accept="application/pdf"
              onChange={onFileChange}
              disabled={uploading}
              ref={fileInputRef}
            />
            {uploading && <MiniLoadingSpinner/>}
          </div>
        </Form.Group>
      </Form>
      )}

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar Menú</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {removing
            ? <MiniLoadingSpinner/>
            : <p>¿Estás seguro de que deseas eliminar el menú?</p>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirm(false)}
            disabled={removing}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={removing}
          >
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MenuViewer;
